'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Eye, EyeOff, CheckCircle2, Zap, BarChart3, Users, ArrowRight,
  AlertCircle, ShieldCheck, ChevronLeft, Smartphone, Download,
  ScanLine, KeyRound, RefreshCw,
} from 'lucide-react';
import Spinner from '@/components/Spinner';
import VividSpinner from '@/components/VividSpinner';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

interface LoginForm { email: string; password: string; }

type Phase = 'password' | '2fa-verify' | '2fa-setup-qr' | '2fa-setup-code' | 'password-expired';

const features = [
  { icon: Zap, text: 'Scrum sprints with burn-down charts' },
  { icon: BarChart3, text: 'Kanban, List, Table & Scrum views' },
  { icon: Users, text: 'Multi-workspace team collaboration' },
  { icon: CheckCircle2, text: 'Bug tracking with priority triage' },
];

const testimonial = {
  quote: "Extio replaced Jira with Nexter for us. The sprint views are exactly what our team needed.",
  author: "Jagadeesh Pilla",
  role: "CIO at Nexter",
  initials: "SG",
  color: "bg-violet-500",
};

const SETUP_STEPS = [
  {
    icon: Download,
    title: 'Get an authenticator app',
    desc: 'Install Google Authenticator, Authy, or 1Password on your phone — they\'re free.',
    detail: 'Search "Google Authenticator" or "Authy" in the App Store or Google Play.',
  },
  {
    icon: ScanLine,
    title: 'Scan the QR code',
    desc: 'Open the app, tap the + button, then select "Scan a QR code".',
    detail: 'Point your phone camera at the QR code shown below. The app will add Extio Nexter automatically.',
  },
  {
    icon: KeyRound,
    title: 'Enter the 6-digit code',
    desc: 'Type the code shown in your authenticator app to confirm setup.',
    detail: 'Codes refresh every 30 seconds — if yours expires, just wait for the next one.',
  },
];

export default function AuthContent() {
  const [phase, setPhase] = useState<Phase>('password');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [success, setSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);
  const [tempToken, setTempToken] = useState('');

  // 2FA verify state
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpError, setTotpError] = useState('');

  // 2FA setup state
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupCode, setSetupCode] = useState('');
  const [setupError, setSetupError] = useState('');
  const [setupBusy, setSetupBusy] = useState(false);

  // Password expiry state
  const [passwordExpiresInDays, setPasswordExpiresInDays] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [pwChangeLoading, setPwChangeLoading] = useState(false);
  const [pwChangeError, setPwChangeError] = useState('');
  const [pwChangeDone, setPwChangeDone] = useState(false);

  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const totpInputRef = useRef<HTMLInputElement>(null);
  const setupCodeRef = useRef<HTMLInputElement>(null);
  const { signIn, dismissLogoutOverlay } = useAuth();

  useEffect(() => {
    setIsHydrated(true);
    dismissLogoutOverlay();
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  useEffect(() => {
    if (phase === '2fa-verify') setTimeout(() => totpInputRef.current?.focus(), 50);
    if (phase === '2fa-setup-code') setTimeout(() => setupCodeRef.current?.focus(), 50);
  }, [phase]);

  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); cooldownRef.current = null; return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '' } });

  // ── Step 1: password submit ──────────────────────────────────────────────────
  const handleLogin = async (data: LoginForm) => {
    if (cooldown > 0) return;
    setLoading(true);
    setAuthError('');
    try {
      const result = await signIn(data.email, data.password);
      if (result?.requiresTwoFactor) {
        setTempToken(result.tempToken);
        setTotpCode('');
        setTotpError('');
        setPhase('2fa-verify');
        return;
      }
      if (result?.requiresTwoFactorSetup) {
        setTempToken(result.tempToken);
        setPhase('2fa-setup-qr');
        loadQrCode(result.tempToken);
        return;
      }
      // No 2FA at all (shouldn't happen in enforced mode but handle gracefully).
      setSuccess(true);
      setRedirecting(true);
      setTimeout(() => { window.location.href = '/workspaces-dashboard'; }, 400);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid credentials')) {
        setAuthError('Incorrect email or password. Please try again.');
      } else if (msg.toLowerCase().includes('rate limit')) {
        setAuthError('Too many sign-in attempts. Please wait before trying again.');
        startCooldown(60);
      } else {
        setAuthError(msg || 'Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Load QR code for setup ───────────────────────────────────────────────────
  const loadQrCode = async (token: string) => {
    setSetupLoading(true);
    setSetupError('');
    try {
      const res = await fetch('/api/auth/2fa/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken: token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate QR code');
      setQrDataUrl(data.qrDataUrl);
      setSecret(data.secret);
    } catch (err: any) {
      setSetupError(err.message || 'Failed to generate QR code');
    } finally {
      setSetupLoading(false);
    }
  };

  // ── After 2FA success — handle password expiry / warning ────────────────────
  const handlePostAuth = (data: any) => {
    const expiresIn: number | undefined = data.passwordExpiresInDays;
    if (expiresIn !== undefined && expiresIn <= 0) {
      // Password expired — force change before entering app
      setPasswordExpiresInDays(expiresIn);
      setPhase('password-expired');
      return;
    }
    if (expiresIn !== undefined && expiresIn <= 3) {
      // Warning: store in sessionStorage so Sidebar can pick it up
      sessionStorage.setItem('passwordExpiresInDays', String(expiresIn));
    } else {
      sessionStorage.removeItem('passwordExpiresInDays');
    }
    setSuccess(true);
    setRedirecting(true);
    setTimeout(() => { window.location.href = '/workspaces-dashboard'; }, 400);
  };

  // ── Password change on expiry ────────────────────────────────────────────────
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword.trim()) return;
    if (newPassword !== confirmPassword) {
      setPwChangeError('Passwords do not match');
      return;
    }
    setPwChangeLoading(true);
    setPwChangeError('');
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      setPwChangeDone(true);
      setTimeout(() => { window.location.href = '/workspaces-dashboard'; }, 1200);
    } catch (err: any) {
      setPwChangeError(err?.message || 'Failed to change password');
    } finally {
      setPwChangeLoading(false);
    }
  };

  // ── Step 3: verify code during setup ────────────────────────────────────────
  const handleSetupVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupCode.trim()) return;
    setSetupBusy(true);
    setSetupError('');
    try {
      const res = await fetch('/api/auth/2fa/enable-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, code: setupCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      handlePostAuth(data);
    } catch (err: any) {
      setSetupError(err?.message || 'Invalid code. Please try again.');
      setSetupCode('');
      setupCodeRef.current?.focus();
    } finally {
      setSetupBusy(false);
    }
  };

  // ── Step 2b: verify existing TOTP ───────────────────────────────────────────
  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpCode.trim()) return;
    setTotpLoading(true);
    setTotpError('');
    try {
      const res = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, code: totpCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      handlePostAuth(data);
    } catch (err: any) {
      setTotpError(err?.message || 'Invalid code. Please try again.');
      setTotpCode('');
      totpInputRef.current?.focus();
    } finally {
      setTotpLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    loginForm.handleSubmit(handleLogin)(e);
  };

  const resetToPassword = () => {
    setPhase('password');
    setTempToken('');
    setTotpCode('');
    setTotpError('');
    setSetupCode('');
    setSetupError('');
    setQrDataUrl('');
    setSecret('');
  };

  return (
    <div className="min-h-screen flex">
      {redirecting && (
        <div className="fixed inset-0 z-50 bg-white" style={{ animation: 'fadeInOverlay 0.4s ease-in forwards' }} />
      )}
      {loading && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm animate-fade-in">
          <VividSpinner size={100} />
          <p className="mt-6 text-sm font-semibold text-gray-500 tracking-wide">Signing in…</p>
        </div>
      )}
      <style>{`@keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }`}</style>

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full" />
          <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 right-12 w-40 h-40 bg-white/5 rounded-full" />
        </div>
        <div className="relative flex items-center gap-3">
          <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center overflow-hidden">
            <AppLogo size={32} />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">Extio Nexter</span>
        </div>
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            Ship faster.<br />Track everything.<br />
            <span className="text-indigo-200">Stay unblocked.</span>
          </h1>
          <p className="text-indigo-200 text-base mb-8 leading-relaxed max-w-md">
            Projects with Nexter everywhere — the project management platform built for engineering teams who care about sprint health, not just task counts.
          </p>
          <div className="space-y-3 mb-10">
            {features.map(f => {
              const FIcon = f.icon;
              return (
                <div key={f.text} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                    <FIcon size={14} className="text-white" />
                  </div>
                  <span className="text-sm text-indigo-100 font-medium">{f.text}</span>
                </div>
              );
            })}
          </div>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
            <p className="text-sm text-white/90 leading-relaxed mb-4 italic">"{testimonial.quote}"</p>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full ${testimonial.color} flex items-center justify-center text-white text-xs font-bold`}>
                {testimonial.initials}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{testimonial.author}</p>
                <p className="text-xs text-indigo-300">{testimonial.role}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="relative flex items-center gap-8">
          {[{ value: '7+', label: 'Teams using Nexter' }, { value: '4.9/5', label: 'Average rating' }, { value: '99.9%', label: 'Uptime SLA' }].map(s => (
            <div key={s.label}>
              <p className="text-xl font-bold text-white font-tabular">{s.value}</p>
              <p className="text-xs text-indigo-300">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-white overflow-y-auto">
        <div className="w-full max-w-md py-6">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-xl text-gray-900">Extio</span>
          </div>

          {/* ── Success ── */}
          {success && (
            <div className="flex flex-col items-center justify-center py-12 animate-fade-in">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 size={28} className="text-green-600" />
              </div>
              <p className="text-lg font-bold text-gray-900 mb-1">Welcome back!</p>
              <p className="text-sm text-gray-500">Redirecting to your workspace...</p>
            </div>
          )}

          {/* ── Phase: password-expired ── */}
          {!success && phase === 'password-expired' && (
            <div className="animate-fade-in">
              <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mb-5">
                <KeyRound size={22} className="text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Your password has expired</h2>
              <p className="text-sm text-gray-500 mb-6">
                Your organisation requires periodic password changes. Please set a new password to continue.
              </p>
              {pwChangeDone ? (
                <div className="flex flex-col items-center py-8 gap-3 animate-fade-in">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 size={24} className="text-green-600" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">Password changed! Redirecting…</p>
                </div>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">New password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        className="input-field pr-10"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        required
                        autoFocus
                      />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm new password</label>
                    <input
                      type="password"
                      className="input-field"
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  {pwChangeError && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 animate-slide-up">
                      <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">{pwChangeError}</p>
                    </div>
                  )}
                  <button type="submit" disabled={pwChangeLoading} className="btn-auth w-full flex items-center justify-center gap-2 disabled:opacity-60">
                    {pwChangeLoading ? <Spinner size={16} className="text-white" /> : <ArrowRight size={16} />}
                    {pwChangeLoading ? 'Saving…' : 'Set new password'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── Phase: email + password ── */}
          {!success && phase === 'password' && (
            <div className="animate-fade-in">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome back</h2>
                <p className="text-sm text-gray-500">Sign in to your Extio account</p>
              </div>
              {authError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4 animate-slide-up">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{authError}</p>
                </div>
              )}
              <form onSubmit={handleFormSubmit} method="post" action="#" className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email address</label>
                  <input
                    type="email"
                    className={`input-field ${loginForm.formState.errors.email ? 'border-red-300' : ''}`}
                    placeholder="you@company.com"
                    {...loginForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-xs text-red-600 mt-1">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-semibold text-gray-700">Password</label>
                    <button type="button" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">Forgot password?</button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`input-field pr-10 ${loginForm.formState.errors.password ? 'border-red-300' : ''}`}
                      placeholder="••••••••"
                      {...loginForm.register('password', { required: 'Password is required', minLength: { value: 6, message: 'At least 6 characters' } })}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-xs text-red-600 mt-1">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={!isHydrated || loading || cooldown > 0}
                  className="btn-primary w-full justify-center py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {(!isHydrated || loading) ? <Spinner size={16} /> : <ArrowRight size={16} />}
                  {!isHydrated ? 'Loading...' : loading ? 'Signing in...' : cooldown > 0 ? `Try again in ${cooldown}s` : 'Sign In'}
                </button>
              </form>
              <p className="text-center text-xs text-gray-400 mt-6">Contact your administrator to get access to Extio.</p>
            </div>
          )}

          {/* ── Phase: enter existing TOTP code ── */}
          {!success && phase === '2fa-verify' && (
            <div className="animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={22} className="text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Two-factor authentication</h2>
                  <p className="text-sm text-gray-500">Enter the 6-digit code from your authenticator app</p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-5">
                <div className="flex items-start gap-2.5">
                  <Smartphone size={15} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-gray-600 space-y-1">
                    <p className="font-semibold text-gray-700">How to find your code</p>
                    <p>Open <span className="font-medium">Google Authenticator</span>, <span className="font-medium">Authy</span>, or any other authenticator app on your phone.</p>
                    <p>Find the entry for <span className="font-medium">Extio Nexter</span> and enter the 6-digit code shown. Codes refresh every 30 seconds.</p>
                  </div>
                </div>
              </div>

              {totpError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{totpError}</p>
                </div>
              )}
              <form onSubmit={handleTotpVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Authenticator code</label>
                  <input
                    ref={totpInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field text-center text-2xl tracking-[0.5em] font-mono"
                    placeholder="000000"
                  />
                </div>
                <button
                  type="submit"
                  disabled={totpLoading || totpCode.length < 6}
                  className="btn-primary w-full justify-center py-2.5 disabled:opacity-60"
                >
                  {totpLoading ? <Spinner size={16} /> : <ShieldCheck size={16} />}
                  {totpLoading ? 'Verifying…' : 'Verify Code'}
                </button>
              </form>
              <button onClick={resetToPassword} className="mt-4 flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                <ChevronLeft size={13} /> Back to sign in
              </button>
            </div>
          )}

          {/* ── Phase: forced first-time setup — show QR code ── */}
          {!success && phase === '2fa-setup-qr' && (
            <div className="animate-fade-in">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={22} className="text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Set up two-factor authentication</h2>
                  <p className="text-sm text-gray-500">Required to access your account</p>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-5">
                Your organisation requires 2FA. This only takes 2 minutes and keeps your account safe.
              </p>

              {/* Numbered steps */}
              <div className="space-y-3 mb-6">
                {SETUP_STEPS.map((step, i) => {
                  const SIcon = step.icon;
                  const isActive = i === 0;
                  return (
                    <div key={i} className={`flex gap-3 rounded-xl p-3.5 border ${i === 0 ? 'border-indigo-200 bg-indigo-50' : 'border-gray-100 bg-gray-50'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${i === 0 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {i + 1}
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${i === 0 ? 'text-indigo-900' : 'text-gray-700'}`}>{step.title}</p>
                        <p className={`text-xs mt-0.5 ${i === 0 ? 'text-indigo-700' : 'text-gray-500'}`}>{step.desc}</p>
                        <p className={`text-xs mt-1 ${i === 0 ? 'text-indigo-600' : 'text-gray-400'}`}>{step.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* QR Code */}
              <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-5 flex flex-col items-center gap-3 mb-5">
                <p className="text-xs font-semibold text-gray-600 text-center">Step 2 · Scan this QR code in your app</p>
                {setupLoading ? (
                  <div className="w-44 h-44 flex items-center justify-center">
                    <VividSpinner size={40} />
                  </div>
                ) : setupError ? (
                  <div className="text-center">
                    <p className="text-xs text-red-600 mb-2">{setupError}</p>
                    <button onClick={() => loadQrCode(tempToken)} className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700">
                      <RefreshCw size={12} /> Try again
                    </button>
                  </div>
                ) : qrDataUrl ? (
                  <>
                    <img src={qrDataUrl} alt="2FA QR code" className="w-44 h-44 rounded-lg" />
                    <div className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-center">
                      <p className="text-[10px] text-gray-400 mb-0.5">Can't scan? Enter this key manually:</p>
                      <p className="font-mono text-xs text-gray-700 tracking-wider select-all break-all">{secret}</p>
                    </div>
                  </>
                ) : null}
              </div>

              <button
                onClick={() => { setSetupCode(''); setSetupError(''); setPhase('2fa-setup-code'); }}
                disabled={setupLoading || !qrDataUrl}
                className="btn-primary w-full justify-center py-2.5 disabled:opacity-60"
              >
                <ArrowRight size={16} />
                I've added it to my app — continue
              </button>

              <button onClick={resetToPassword} className="mt-3 flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 w-full justify-center">
                <ChevronLeft size={13} /> Back to sign in
              </button>
            </div>
          )}

          {/* ── Phase: enter setup verification code ── */}
          {!success && phase === '2fa-setup-code' && (
            <div className="animate-fade-in">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <KeyRound size={22} className="text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Confirm your authenticator</h2>
                  <p className="text-sm text-gray-500">Final step — enter the code to complete setup</p>
                </div>
              </div>

              {/* Step reminder */}
              <div className="space-y-2 mb-5">
                {SETUP_STEPS.map((step, i) => {
                  const SIcon = step.icon;
                  const done = i < 2;
                  const active = i === 2;
                  return (
                    <div key={i} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${active ? 'border-indigo-200 bg-indigo-50' : done ? 'border-green-100 bg-green-50' : 'border-gray-100 bg-gray-50'}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${active ? 'bg-indigo-600 text-white' : done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {done ? <CheckCircle2 size={13} /> : i + 1}
                      </div>
                      <div>
                        <p className={`text-xs font-semibold ${active ? 'text-indigo-800' : done ? 'text-green-700' : 'text-gray-600'}`}>{step.title}</p>
                        <p className={`text-xs ${active ? 'text-indigo-600' : done ? 'text-green-600' : 'text-gray-400'}`}>{active ? step.detail : done ? 'Done' : step.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {setupError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{setupError}</p>
                </div>
              )}

              <form onSubmit={handleSetupVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Enter the 6-digit code from your authenticator app
                  </label>
                  <input
                    ref={setupCodeRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={setupCode}
                    onChange={e => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field text-center text-2xl tracking-[0.5em] font-mono"
                    placeholder="000000"
                  />
                  <p className="text-xs text-gray-400 mt-1.5">Find the "Extio Nexter" entry in your app and type the code shown.</p>
                </div>
                <button
                  type="submit"
                  disabled={setupBusy || setupCode.length < 6}
                  className="btn-primary w-full justify-center py-2.5 disabled:opacity-60"
                >
                  {setupBusy ? <Spinner size={16} /> : <ShieldCheck size={16} />}
                  {setupBusy ? 'Activating…' : 'Activate & Sign In'}
                </button>
              </form>

              <button
                onClick={() => { setSetupCode(''); setSetupError(''); setPhase('2fa-setup-qr'); }}
                className="mt-3 flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors w-full justify-center"
              >
                <ChevronLeft size={13} /> Back to QR code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
